'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetUserField } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import { EntityTag } from '@/components/search/entity-tag';
import { RowAside } from '@/components/search/search-row-parts';
import { SearchRowSubline } from '@/components/search/search-row-subline';
import { useCommandLink } from '@/components/search/use-command-link';
import { CommandItem } from '@/components/ui/app-command';
import { useFormattedCurrency } from '@/lib/currency';
export type UserSearchRowVariant = 'creator' | 'user';
interface UserSearchRowProps {
  variant: UserSearchRowVariant;
  rowKey: string;
  identifier: string;
  walletAddress: string;
  userId: string | null;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw: boolean;
  avatarPreferences: {
    rasterise: boolean;
  } | null;
  totalVolume?: string;
  totalTrades?: number;
  totalMarketsCreated?: number;
  /** Signed PnL (D6) snapshot from the search response. */
  totalPnl?: string;
  /** Admin-managed partner creator — replaces the neutral Trader tag. */
  partner?: boolean;
  href: string;
  onCommit: () => void;
  onHoverPrefetch?: (identifier: string) => void;
}
function safeBigInt(value: string | undefined): bigint | null {
  if (value === undefined || value === '') return null;
  try {
    return BigInt(value);
  } catch {
    // SILENT-CATCH: non-numeric → absent
    return null;
  }
}
export function UserSearchRow(props) {
  const $ = _c(57);
  let t0;
  if ($[0] !== props.identifier) {
    t0 = {
      identifier: props.identifier
    };
    $[0] = props.identifier;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const liveTotalVolume = useGetUserField(t0, "totalVolume");
  const t = useTranslations("search");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const {
    onSelect,
    overlay
  } = useCommandLink(props.href, props.onCommit);
  const isCreator = props.variant === "creator";
  const volume = liveTotalVolume ?? props.totalVolume ?? "0";
  let t1;
  if ($[2] !== props.totalMarketsCreated || $[3] !== t) {
    t1 = (props.totalMarketsCreated ?? 0) > 0 ? t("markets_created", {
      count: props.totalMarketsCreated ?? 0
    }) : null;
    $[2] = props.totalMarketsCreated;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const marketsPiece = t1;
  let t2;
  if ($[5] !== formatD6 || $[6] !== isCreator || $[7] !== marketsPiece || $[8] !== props.totalTrades || $[9] !== t || $[10] !== volume) {
    t2 = isCreator ? [marketsPiece] : [t("trades_count", {
      count: props.totalTrades ?? 0
    }), t("volume_label", {
      volume: formatD6(volume, "compact")
    }), marketsPiece];
    $[5] = formatD6;
    $[6] = isCreator;
    $[7] = marketsPiece;
    $[8] = props.totalTrades;
    $[9] = t;
    $[10] = volume;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const sublinePieces = t2;
  const pnlBig = safeBigInt(props.totalPnl);
  let t3;
  if ($[12] !== pnlBig) {
    t3 = pnlBig !== null && pnlBig !== BigInt(0);
    $[12] = pnlBig;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const hasPnl = t3;
  const pnlTone = pnlBig !== null && pnlBig < BigInt(0) ? "destructive" : "success";
  let t4;
  let t5;
  if ($[14] !== props) {
    t4 = () => props.onHoverPrefetch?.(props.identifier);
    t5 = () => props.onHoverPrefetch?.(props.identifier);
    $[14] = props;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  const t6 = props.userId ?? props.walletAddress;
  const t7 = props.avatarPreferences?.rasterise ?? false;
  let t8;
  if ($[17] !== props.avatarNsfw || $[18] !== props.avatarUrl || $[19] !== props.displayName || $[20] !== t6 || $[21] !== t7) {
    t8 = <UserAvatar size="sm" userId={t6} displayName={props.displayName} avatarUrl={props.avatarUrl} nsfw={props.avatarNsfw} rasterise={t7} />;
    $[17] = props.avatarNsfw;
    $[18] = props.avatarUrl;
    $[19] = props.displayName;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== props.avatarNsfw || $[25] !== props.avatarUrl || $[26] !== props.displayName || $[27] !== props.handle || $[28] !== props.userId || $[29] !== props.walletAddress) {
    t10 = <UserIdentity user={{
      userId: props.userId,
      walletAddress: props.walletAddress,
      displayName: props.displayName,
      handle: props.handle,
      avatarUrl: props.avatarUrl,
      avatarNsfw: props.avatarNsfw,
      connectedProviders: t9
    }} size="sm" className="truncate" />;
    $[24] = props.avatarNsfw;
    $[25] = props.avatarUrl;
    $[26] = props.displayName;
    $[27] = props.handle;
    $[28] = props.userId;
    $[29] = props.walletAddress;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const t11 = props.partner ? "partner" : isCreator ? "creator" : "trader";
  let t12;
  if ($[31] !== t11) {
    t12 = <EntityTag kind={t11} />;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t10 || $[34] !== t12) {
    t13 = <div className="flex min-w-0 items-center gap-2">{t10}{t12}</div>;
    $[33] = t10;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== sublinePieces) {
    t14 = <SearchRowSubline pieces={sublinePieces} />;
    $[36] = sublinePieces;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t13 || $[39] !== t14) {
    t15 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t13}{t14}</div>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== formatD6Signed || $[42] !== hasPnl || $[43] !== pnlTone || $[44] !== props.totalPnl || $[45] !== t) {
    t16 = hasPnl ? <RowAside value={formatD6Signed(props.totalPnl ?? "0", "compact")} label={t("label_pnl")} tone={pnlTone} /> : null;
    $[41] = formatD6Signed;
    $[42] = hasPnl;
    $[43] = pnlTone;
    $[44] = props.totalPnl;
    $[45] = t;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== onSelect || $[48] !== overlay || $[49] !== props.rowKey || $[50] !== props.walletAddress || $[51] !== t15 || $[52] !== t16 || $[53] !== t4 || $[54] !== t5 || $[55] !== t8) {
    t17 = <CommandItem key={props.rowKey} value={props.walletAddress} onSelect={onSelect} onMouseEnter={t4} onFocus={t5} className="relative gap-3 rounded-overlay-item px-3 py-2.5">{overlay}{t8}{t15}{t16}</CommandItem>;
    $[47] = onSelect;
    $[48] = overlay;
    $[49] = props.rowKey;
    $[50] = props.walletAddress;
    $[51] = t15;
    $[52] = t16;
    $[53] = t4;
    $[54] = t5;
    $[55] = t8;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  return t17;
}